// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#030712] preserve le rendu dark historique (voir memoire ui-themes)
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { supportTicketService } from "@core/services/SupportTicketService";
import { supportFeedbackService } from "@core/services/SupportFeedbackService";
import { FeedbackCollector } from "../components/support/FeedbackCollector";
import { AlertCircle, Loader2 } from "lucide-react";

export const SupportFeedbackPage: React.FC = () => {
  const { t } = useTranslation(["polls", "common", "errors"]);
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticket, setTicket] = useState<{ id: string; subject: string } | null>(
    null,
  );
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketId) {
      setError(t("errors:support_invalid_link"));
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        // RPC dédiée : la page est publique (lien e-mail, souvent sans
        // compte) et la table n'est plus lisible sans session.
        const [feedbackTicket, existingFeedback] = await Promise.all([
          supportTicketService.getFeedbackTicket(ticketId),
          supportFeedbackService.getFeedbackByTicket(ticketId),
        ]);
        setTicket(feedbackTicket);
        setAlreadySubmitted(!!existingFeedback);
      } catch {
        setError(t("errors:support_link_invalid_or_expired"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [ticketId, t]);

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <span className="text-foreground font-semibold text-lg">
            DooDates
          </span>
          <span className="text-muted-foreground text-sm">
            {t("polls:support")}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-muted-foreground text-sm">
                {t("common:loading")}
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-destructive" />
              </div>
              <h1 className="text-xl font-semibold text-foreground">
                {t("errors:support_invalid_link_title")}
              </h1>
              <p className="text-muted-foreground text-sm max-w-xs">{error}</p>
            </div>
          )}

          {!loading && !error && ticket && (
            <FeedbackCollector
              ticketId={ticketId!}
              ticketSubject={ticket.subject}
              alreadySubmitted={alreadySubmitted}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border px-6 py-4 text-center">
        <p className="text-muted-foreground text-xs">
          {t("polls:support_footer_tagline")}
        </p>
      </footer>
    </div>
  );
};
